import React from 'react';
import clsx from 'clsx';
import { MessageDescriptor, useIntl } from 'react-intl';
import type { LyricSyncState } from 'fym-shared/src/music24/lyricTiming';
import { TypedMemo } from '../../../../common/types';
import { fullVideoMessages } from '../fullVideoMessages';
import { StudioSongFacts } from './props';
import { DetailField } from './DetailField';
import type { SongLyricsView } from '../../../../smartLinks/catalog/song/page/catalogSongPath';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from '../Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

const PILLS: Readonly<Record<LyricSyncState, { message: MessageDescriptor; className: string | undefined }>> = {
  synced: { message: fullVideoMessages.pillSynced, className: styles.chipOk },
  syncing: { message: fullVideoMessages.pillSyncing, className: styles.chipRun },
  failed: { message: fullVideoMessages.pillFailed, className: styles.chipBad },
  none: { message: fullVideoMessages.pillNoLyrics, className: styles.chipOff },
};

/** The sync state that the row shows: lyrics with no timing yet are about to sync. */
export function lyricsRowState(song: Pick<StudioSongFacts, 'hasLyrics' | 'lyricSyncState'>): LyricSyncState {
  if (!song.hasLyrics) {
    return 'none';
  }
  const state = song.lyricSyncState ?? 'none';
  return state === 'none' ? 'syncing' : state;
}

interface FullVideoLyricsRowProps {
  /** The music video shows the lines as captions; with no lyrics it has none. */
  captions: boolean;
  song: StudioSongFacts;
  onOpenSongLyrics: (view?: SongLyricsView) => void;
}

/**
 * "Lyrics" of a lyric video or a music video (design s7): the sync state (Synced, Syncing, Failed,
 * No lyrics), the counts of the timing, and "Check the timing", which opens the Lyrics section of the song page in Catalog.
 */
const FullVideoLyricsRowComponent: React.FC<FullVideoLyricsRowProps> = ({ captions, song, onOpenSongLyrics }) => {
  const intl = useIntl();
  const state = lyricsRowState(song);
  const summary = song.lyricTiming ?? null;
  const pill = PILLS[state];

  let line: string;
  if (state === 'synced' && summary !== null) {
    line =
      summary.editedCount > 0
        ? intl.formatMessage(fullVideoMessages.lyricsLinesEdited, {
            count: summary.lineCount,
            edited: summary.editedCount,
          })
        : intl.formatMessage(fullVideoMessages.lyricsLines, { count: summary.lineCount });
  } else if (state === 'syncing') {
    line = intl.formatMessage(fullVideoMessages.lyricsSyncingLine);
  } else if (state === 'failed') {
    line = intl.formatMessage(fullVideoMessages.lyricsFailedLine);
  } else if (state === 'none') {
    line = intl.formatMessage(captions ? fullVideoMessages.lyricsNoneCaptions : fullVideoMessages.needsLyricsBody);
  } else {
    line = '';
  }
  const checkCount = state === 'synced' && summary !== null ? summary.checkCount : 0;

  return (
    <DetailField
      title={intl.formatMessage(fullVideoMessages.lyricsTitle)}
      hint={intl.formatMessage(captions ? fullVideoMessages.lyricsHintCaptions : fullVideoMessages.lyricsHint)}
    >
      <div className={styles.lyricsRow} data-testid="studio-lyrics-row" data-state={state}>
        <span className={clsx(styles.chip, pill.className)}>{intl.formatMessage(pill.message)}</span>
        <span className={styles.lyricsRowText}>
          <b>{line}</b>
          {checkCount > 0 && (
            <small className={styles.mono}>
              {intl.formatMessage(fullVideoMessages.lyricsCheck, { count: checkCount })}
            </small>
          )}
        </span>
        <button
          type="button"
          className="btn btn-link btn-sm p-0"
          onClick={() => onOpenSongLyrics(state === 'none' ? 'text' : undefined)}
        >
          {intl.formatMessage(state === 'none' ? fullVideoMessages.addLyrics : fullVideoMessages.checkTiming)}
        </button>
      </div>
    </DetailField>
  );
};

export const FullVideoLyricsRow = TypedMemo(FullVideoLyricsRowComponent);
